<template>
  <div class="ml-4 mr-4">
    <div v-if="type === 'create'">
      <div class="row py-3 align-items-center">
        <div class="col company-h1">
          <button type="button" class="btn btn-back" @click="setCompanyStep('companies-map')" />
          Create Company
        </div>
        <div class="col text-right" v-if="$_can('companies.update')">
          <button type="button" class="btn btn-primary ld-ext-right"
                  @click="save"
                  :disabled="buttonDisabled"
                  :class="loadingButton ? 'running' : ''"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
      <companies-steps-menu type="create" :saved-settings="savedSettings" />
    </div>
    <div v-if="type === 'edit'">
      <div class="row py-3 align-items-center company-header">
        <div class="col-4">
          <h2 class="company-h1 mr-3">
            <button type="button" class="btn btn-back" @click="$router.push('/companies')" />
            {{ company.name }}
          </h2>
          <template v-if="company.status">
            <span v-if="company.status.name === 'Active'"
                  :class="'btn-status btn-active'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Inactive'"
                  :class="'btn-status btn-closed'"
            >{{ company.status.name }}</span>
            <span v-if="company.status.name === 'Setup Incomplete'"
                  :class="'btn-status btn-ncover info-company'"
            >{{ company.status.name }}
              <div class="btn btn-link p-0 tippy-popup tippy-info" @mouseover="showTooltipPrefix = true"
                   @mouseout="showTooltipPrefix = false"
              >
                <i class="fa fa-info-circle" />
              </div>
              <div class="popover tippy-info-popover" v-if="showTooltipPrefix">
                Setup incomplete is present when the company doesn't have one or more of the following: move sizes, arrival windows, merchant account, rates and coverage.
              </div>
            </span>
          </template>
        </div>
        <div class="col-8 d-flex align-items-center justify-content-end">
          <companies-visibility-bar
            :company="company"
            @saved="$emit('saved')"
            v-if="$_can('companies.review.request')"
          />
          <button type="button"
                  class="btn btn-primary ld-ext-right ml-5"
                  :class="loadingButton ? 'running' : ''"
                  :disabled="buttonDisabled"
                  @click="save"
                  v-if="$_can('companies.update')"
          >
            Save
            <div class="ld ld-ball ld-flip" />
          </button>
        </div>
      </div>
    </div>

    <div class="row mb-5">
      <div class="col-6 move-sizes">
        <div class="l-header-2">Move sizes</div>
        <error-field :errors="errors" :field="`move_sizes`" />
        <div class="move-sizes-wrap border rounded-top user-role">
          <div
            v-for="(size, index) in moveSizesAll"
            :key="size.id"
          >
            <div class="row">
              <div class="col-md-8">
                <div class="check-field">
                  <label :for="'move_size-' + index"
                         :class="[{'check-open' : move_sizes.find((el) => el.id === size.id)},
                                  {'disabled': !$_can('companies.move-size.update')}]"
                  >
                    <span class="checkmark" />{{ size.name }}
                    <input type="checkbox"
                           v-model="move_sizes"
                           :value="size"
                           :id="'move_size-' + index"
                           @click="clearError($event)"
                           class="btn-check"
                    />
                  </label>
                </div>
              </div>
              <div class="col-md-4">
                <div class="check-field">
                  <label :for="'small_job-' + index"
                         :class="[{'check-open' : move_sizes.find((el) => el.id === size.id) && move_sizes.find((el) => el.id === size.id).small_job === 1},
                                  {'disabled': !$_can('companies.move-size.update') || !move_sizes.find((el) => el.id === size.id)}]"
                         @click.prevent="changeSmallJob(move_sizes.find((el) => el.id === size.id))"
                  >
                    <span class="checkmark" />Small job
                    <input type="checkbox"
                           :id="'small_job-' + index"
                           @click="clearError($event)"
                           class="btn-check"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-6" v-if="$_can('companies.arrival-windows.index')">
        <div class="l-header-2">Arrival windows</div>
        <div class="row mb-2"
             v-for="(arrival_window, index) in arrival_windows"
             :key="arrival_window.id"
        >
          <div class="col-3 pr-1">
            <div class="select-wrap">
              <label>Name</label>
              <input v-model="arrival_window.name"
                     class="form-control"
                     :disabled="!$_can('companies.arrival-windows.update')"
              />
            </div>
            <error-field :errors="errors" :field="`${index}.name`" />
          </div>
          <div class="col-3 px-1">
            <div class="select-wrap">
              <label>From</label>
              <simple-select v-model="arrival_window.arrival_from"
                             :options="arrival_windows_list"
                             return-value=""
                             placeholder="Select Arrival Window"
                             :disabled="!$_can('companies.arrival-windows.update')"
              />
            </div>
            <error-field :errors="errors" :field="`${index}.from`" />
          </div>
          <div class="col-3 pl-1">
            <div class="select-wrap">
              <label>To</label>
              <simple-select v-model="arrival_window.arrival_to"
                             :options="arrival_windows_list"
                             return-value=""
                             placeholder="Select Arrival Window"
                             :disabled="!$_can('companies.arrival-windows.update')"
              />
            </div>
            <error-field :errors="errors" :field="`${index}.to`" />
          </div>
          <div class="col-3" v-if="$_can('companies.arrival-windows.destroy')">
            <button type="button" v-if="arrival_windows.length > 1"
                    @click="removeArrivalWindow(index)"
                    class="btn btn-clear mt-3"
            >
              <span class="mod-icon-clear" />
            </button>
          </div>
        </div>
        <div class="row mt-3" v-if="$_can('companies.arrival-windows.store')">
          <div class="col">
            <button type="button"
                    class="btn add-button"
                    @click="addArrivalWindow()"
            >
              <span>
                <span class="mod-icon-add" />
              </span>Add Arrival Window
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="row mb-5">
      <div class="col-12">
        <div class="l-header-2">Booking options</div>
        <div class="booking-wrap move-sizes-wrap border rounded-top user-role">
          <div class="row">
            <div class="check-field">
              <label :class="[company.booking_phone ? 'check-open' : 'check-closed']" for="book_phone">
                <span class="checkmark" />Book through phone
                <input type="checkbox"
                       id="book_phone"
                       v-model="company.booking_phone"
                       @click="clearError($event)"
                       class="btn-check"
                />
              </label>
            </div>
          </div>
          <div class="row">
            <div class="check-field">
              <label :class="[company.booking_send_offers ? 'check-open' : 'check-closed']" for="book_offer">
                <span class="checkmark" />Send offers to customer
                <input type="checkbox"
                       id="book_offer"
                       v-model="company.booking_send_offers"
                       @click="clearError($event)"
                       class="btn-check"
                />
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-4" v-if="$_can('companies.payment-profiles.index')">
        <div class="l-header-2">Payment gateway</div>
        <div class="py-3 px-2 pointer d-flex align-items-center payment-wrap"
             @click="$_can('companies.payment-profiles.update') && openGatewayModal('edit')"
             v-if="company.payment_gateway !== undefined"
        >
          <span class="mod-icon-stripe"
                v-if="paymentProfile.payment_gateway_id === 1"
          />
          <span class="mod-icon-authorize"
                v-if="paymentProfile.payment_gateway_id === 2"
          />
          <span class="payment-name">{{ company.payment_gateway }}<br /><small>{{ paymentProfile.name }}</small></span>
          <div class="payment-delete pointer ml-auto" @click.stop="removePaymentGateway"><i class="fa fa-trash" /></div>
        </div>
        <div v-else>
          <button class="btn add-button"
                  type="button"
                  @click="openGatewayModal('edit')"
                  v-if="$_can('companies.payment-profiles.store')"
          >
            <span><span class="mod-icon-add" /></span>
            Select merchant account
          </button>
        </div>
      </div>
    </div>

    <!-- Edit Payment gateway -->
    <modal v-if="showSelectGatewayModal" @close="closeModal" size="modal-large">
      <div slot="header">
        <h4>Add payment profile</h4>
      </div>
      <div slot="body" style="">
        <template>
          <div class="row">
            <div class="col-12">
              <label class="m-label">Select payment gateway</label>
              <div class="select-wrap mb-2">
                <label class="m-label">Payment processor</label>
                <simple-select v-model="paymentProfile.payment_gateway_id"
                               :options="paymentGateways"
                               placeholder="Select Payment Processor"
                               @input="loadPaymentProfiles"
                />
              </div>
              <error-field :errors="errors" field="payment_gateway_id" />
            </div>
            <div class="col-12">
              <label class="m-label">Select profile</label>
              <div class="select-wrap mb-2">
                <label class="m-label">Payment profile</label>
                <simple-select v-model="paymentProfile.id"
                               :options="paymentProfiles"
                               :custom-label="customLabel"
                               placeholder="Select Payment Processor"
                               @input="errors.clear('payment_profile_id')"
                />
              </div>
              <error-field :errors="errors" field="payment_profile_id" />
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <button type="button"
                      class="btn add-button"
                      @click="openGatewayModal('create')"
                      v-if="$_can('companies.payment-profiles.store')"
              >
                <span>
                  <span class="mod-icon-add" />
                </span>Create new
              </button>
              <button type="button" class="btn btn-primary ld-ext-right pull-right"
                      :class="loadingButton ? 'running' : ''"
                      :disabled="buttonDisabled"
                      @click="assignPaymentGateway"
              >
                Save
                <div class="ld ld-ball ld-flip" />
              </button>
            </div>
          </div>
        </template>
      </div>
      <div slot="footer">
        <error-field :errors="errors" field="company_id" />
      </div>
    </modal>

    <!-- Create payment gateway-->
    <modal v-if="showCreateGatewayModal" @close="closeModal" size="modal-large">
      <div slot="header">
        <h4>Add new gateway</h4>
      </div>
      <div slot="body" style="">
        <template>
          <div class="row">
            <div class="col-12">
              <div class="select-wrap mb-2">
                <label class="m-label">Payment processor</label>
                <simple-select v-model="newPaymentProfile.payment_gateway_id"
                               :options="paymentGateways"
                               placeholder="Select Payment Processor"
                               @input="errors.clear('payment_gateway_id')"
                />
              </div>
              <error-field :errors="errors" field="payment_gateway_id" />
            </div>
            <div class="col-12">
              <div class="select-wrap mb-2">
                <label class="m-label">Name</label>
                <input type="text" class="form-control"
                       v-model="newPaymentProfile.name"
                       @input="errors.clear('name')"
                />
              </div>
              <error-field :errors="errors" field="name" />
              <div v-show="newPaymentProfile.payment_gateway_id === 1">
                <label class="m-label">Credentials</label>
                <div class="select-wrap mb-2">
                  <label class="m-label">Secret key</label>
                  <input type="text" class="form-control" name="apiKey"
                         v-model="newPaymentProfile.credentials.apiKey"
                         @input="errors.clear('credentials')"
                  />
                </div>
                <div class="select-wrap mb-2">
                  <label class="m-label">Publishable key</label>
                  <input type="text" class="form-control" name="publicKey"
                         v-model="newPaymentProfile.credentials.publicKey"
                         @input="errors.clear('credentials')"
                  />
                </div>
              </div>
              <div v-show="newPaymentProfile.payment_gateway_id === 2">
                <label class="m-label">Credentials</label>
                <div class="select-wrap mb-2">
                  <label class="m-label">Client key</label>
                  <input type="text" class="form-control" name="clientKey"
                         v-model="newPaymentProfile.credentials.clientKey"
                         @input="errors.clear('credentials')"
                  />
                </div>
                <div class="select-wrap mb-2">
                  <label class="m-label">Login ID</label>
                  <input type="text" class="form-control" name="apiLoginID"
                         v-model="newPaymentProfile.credentials.apiLoginID"
                         @input="errors.clear('credentials')"
                  />
                </div>
                <div class="select-wrap mb-2">
                  <label class="m-label">Transaction key</label>
                  <input type="text" class="form-control" name="transactionKey"
                         v-model="newPaymentProfile.credentials.transactionKey"
                         @input="errors.clear('credentials')"
                  />
                </div>
              </div>
              <error-field :errors="errors" field="credentials" />
            </div>
          </div>
          <div class="row">
            <div class="col-12">
              <button type="button" class="btn btn-primary ld-ext-right pull-right"
                      :class="loadingButton ? 'running' : ''"
                      :disabled="buttonDisabled"
                      @click="createPaymentGateway"
              >
                Add
                <div class="ld ld-ball ld-flip" />
              </button>
            </div>
          </div>
        </template>
      </div>
      <div slot="footer">
        <error-field :errors="errors" field="company_id" />
      </div>
    </modal>
  </div>
</template>

<script>
import axios from 'axios';
import toastr from 'toastr';
import { mapActions, mapMutations, mapState } from 'vuex';
import Errors from '../../classes/Errors';
import ErrorField from '../ErrorField';
import CompaniesStepsMenu from './CompaniesStepsMenu';
import Modal from '../Modal';
import CompaniesVisibilityBar from './CompaniesVisibilityBar';

export default {
  components: {
    CompaniesVisibilityBar,
    Modal,
    CompaniesStepsMenu,
    ErrorField,
  },
  props: {
    type: {
      type: String,
      default: 'edit',
    },
    company: {
      type: Object,
      default: () => ({
        address2: '',
        address: '',
        cash_discount_unit: '',
        cash_discount_value: '',
        city: '',
        custom_rate: null,
        default_carrier_id: '',
        deposit_unit: '$',
        deposit_value: '',
        email: '',
        fixed_rate: 1,
        meta_description: '',
        meta_title: '',
        minimum_hours: '',
        name: '',
        offer_duration: 2,
        parking_address: '',
        parking_address2: '',
        parking_full_address: '',
        parking_city: '',
        parking_latitude: '',
        parking_longitude: '',
        parking_state: '',
        parking_state_id: '',
        parking_zip: '',
        phone: '',
        ref_id_prefix: '',
        state_id: '',
        truck_price: '',
        zip: '',
      }),
    },
  },
  data: () => ({
    move_sizes: [],
    arrival_windows: [
      {
        name: '',
        arrival_from: '',
        arrival_to: '',
      },
    ],
    arrival_windows_list: [],
    paymentGateways: [],
    paymentProfiles: [],
    notifications: [],
    showCreateGatewayModal: false,
    showSelectGatewayModal: false,
    paymentProfile: {
      name: '',
      payment_gateway_id: '',
      payment_gateway_name: '',
      company: {},
      credentials: {},
    },
    newPaymentProfile: {
      name: '',
      payment_gateway_id: '',
      payment_gateway_name: '',
      company: {},
      credentials: {},
    },
    addingVariableOn: 'subject',
    loadingButton: false,
    buttonDisabled: false,
    showTooltipPrefix: false,
    editedTemplates: [],
    savedSettings: false,
    errors: new Errors(),
  }),
  computed: {
    ...mapState({
      companies: state => state.companies.companies.data,
      moveSizesAll: state => state.values.moveSizes,
    }),
  },
  created() {
    this.arrival_windows_list = this.arrivalWindowsList();
    this.loadSettings();
    this.getPaymentGateways();
    if (this.type === 'edit') {
      this.getCompanyPaymentGateway();
      this.moveSizesAll.map(el => {
        el.small_job = 0;
        return el;
      });
    }
  },
  methods: {
    changeSmallJob(moveSize) {
      moveSize.small_job = moveSize.small_job === 0 ? 1 : 0;
    },
    ...mapActions(['getCompanies']),
    ...mapMutations(['setCompanyStep', 'setCompany', 'setSavedSettings']),
    arrivalWindowsList() {
      const list = [];
      for (let i = 0; i < 12; i += 1) {
        if (i === 0) {
          list.push('12:00 AM');
        } else {
          list.push(`${i}:00 AM`.padStart(8, '0'));
        }
      }
      for (let i = 0; i < 12; i += 1) {
        if (i === 0) {
          list.push('12:00 PM');
        } else {
          list.push(`${i}:00 PM`.padStart(8, '0'));
        }
      }
      return list;
    },
    addArrivalWindow() {
      this.arrival_windows.push({
        name: '',
        arrival_from: '',
        arrival_to: '',
      });
    },
    removeArrivalWindow(index) {
      this.arrival_windows.splice(index, 1);
    },
    getPaymentGateways() {
      axios
        .get('/values/payment-gateways')
        .then(res => {
          this.paymentGateways = res.data.data;
        })
        .catch(err => this.errors.record(err.response.data.error));
    },
    getCompanyPaymentGateway() {
      if (this.company.payment_profile_id) {
        axios
          .get(`/companies/payment-profiles/${this.company.payment_profile_id}`)
          .then(res => {
            this.paymentProfile = res.data.data;
            this.loadPaymentProfiles(false);
          })
          .catch(err => this.errors.record(err.response.data.error));
      }
    },
    customLabel(option) {
      return `${option.name ? option.name : option.payment_gateway_name}`;
    },
    loadPaymentProfiles(reset = true) {
      this.errors.clear('payment_gateway_id');
      if (reset) {
        this.paymentProfile.id = null;
      }
      if (this.paymentProfile.payment_gateway_id) {
        axios
          .get(
            `/companies/payment-profiles?payment_gateway_id=${
              this.paymentProfile.payment_gateway_id
            }`,
          )
          .then(res => {
            this.paymentProfiles = res.data.data;
          })
          .catch(err => this.errors.record(err.response.data.error));
      } else {
        this.paymentProfiles = [];
      }
    },
    save() {
      this.buttonDisabled = true;
      this.loadingButton = true;
      axios
        .post(`/companies/${this.company.id}/move-sizes`, { move_sizes: this.move_sizes })
        .then(() => {
          axios.patch(`/companies/${this.company.id}`, {
            email_copy: this.company.email_copy,
            booking_phone: this.company.booking_phone,
            booking_send_offers: this.company.booking_send_offers,
          });
          this.saveArrivalWindows(this.arrival_windows);
          this.getCompanies();
          this.$emit('saved');
          this.setSavedSettings(true);
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    createPaymentGateway() {
      this.buttonDisabled = true;
      this.loadingButton = true;
      axios
        .post(`/companies/payment-profiles`, {
          payment_gateway_id: this.newPaymentProfile.payment_gateway_id,
          name: this.newPaymentProfile.name,
          company_id: this.company.id,
          credentials: this.newPaymentProfile.credentials,
        })
        .then(res => {
          toastr.success('Payment gateway saved');
          this.closeModal();
          this.errors.clear();
          this.$emit('saved');
          this.getCompanies();
          this.newPaymentProfile = {
            name: '',
            payment_gateway_id: '',
            payment_gateway_name: '',
            company: {},
            credentials: {},
          };
          this.paymentProfile = res.data.data;
          this.getCompanies();
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    assignPaymentGateway() {
      this.buttonDisabled = true;
      this.loadingButton = true;
      axios
        .post(`/companies/payment-profiles/assign`, {
          payment_profile_id: this.paymentProfile.id,
          company_id: this.company.id,
        })
        .then(() => {
          toastr.success('Payment gateway saved');
          this.closeModal();
          this.errors.clear();
          this.$emit('saved');
          this.getCompanyPaymentGateway();
          this.getCompanies();
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        })
        .finally(() => {
          this.loadingButton = false;
          this.buttonDisabled = false;
        });
    },
    removePaymentGateway() {
      axios
        .delete(`companies/payment-profiles/remove`, {
          data: {
            company_id: this.company.id,
          },
        })
        .then(() => {
          toastr.success('Payment gateway deleted');
          this.closeModal();
          this.paymentProfile = {
            payment_gateway_id: '',
            payment_gateway_name: '',
            name: '',
            company: {},
            credentials: {},
          };
          this.newPaymentProfile = this.paymentProfile;
          this.$emit('saved');
          this.getCompanyPaymentGateway();
          this.getCompanies();
        })
        .catch(err => err.message);
    },
    mapArrivalWindows(arrivalWindows) {
      return arrivalWindows.map(window => {
        return {
          name: window.name,
          from: window.arrival_from,
          to: window.arrival_to,
        };
      });
    },
    saveArrivalWindows(arrivalWindows) {
      const arrivalWindowsMap = this.mapArrivalWindows(arrivalWindows);
      axios
        .post(`/companies/${this.company.id}/arrival-windows/save`, arrivalWindowsMap)
        .then(() => {
          toastr.success('Successfully saved move sizes and arrival windows');
          if (this.type === 'create') {
            this.setCompanyStep('companies-pricing');
          }
          this.$emit('saved');
        })
        .catch(err => {
          this.errors.record(err.response.data.error);
        });
    },
    loadSettings() {
      const id = this.$route.params.id ? this.$route.params.id : this.company.id;
      axios
        .get(`/companies/${id}/move-sizes`)
        .then(res => {
          this.move_sizes = res.data.data;
          this.move_sizes.map(el => {
            delete el.mover_numbers;
            return el;
          });
        })
        .catch(err => err.message);
      axios
        .get(`companies/${id}/arrival-windows`)
        .then(res => {
          this.arrival_windows = res.data.data.map(window => {
            return {
              name: window.name,
              arrival_from: window.from,
              arrival_to: window.to,
            };
          });
        })
        .catch(err => err.message);
    },
    closeModal() {
      this.showSelectGatewayModal = false;
      this.showCreateGatewayModal = false;
      this.errors.clear();
      this.newPaymentProfile = {
        payment_gateway_id: '',
        payment_gateway_name: '',
        name: '',
        company: {},
        credentials: {},
      };
    },
    clearError(event) {
      this.errors.clear(event.target.name);
    },
    openGatewayModal(type) {
      if (type === 'create') {
        this.showSelectGatewayModal = false;
        this.showCreateGatewayModal = true;
      } else {
        this.showSelectGatewayModal = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.booking-wrap {
  justify-content: start;
  flex-direction: column;
  padding-left: 14px;
}
</style>



// WEBPACK FOOTER //
// src/components/companies/CompaniesSettings.vue